import { useContext, useState, useEffect } from "react";
import { useHistory } from "react-router-dom";

import SpotReviewContext from "../../../context/spot/spotReviewContext";
import AlertContext from "../../../context/alert/alertContext";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";

import { Box, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Step, StepLabel, Stepper, Typography } from "@mui/material";
import FindPlaceCreationStep from "./FindPlaceCreationStep"
import UploadPhotoCreationStep from "./UploadPhotoCreationStep";
import WriteContentCreationStep from "./WriteContentCreationStep"
import SpotReviewPreview from "../SpotReviewPreview";

const FULL_STEPS = ['Choose a location', 'Upload photos', 'Write something'];
const EDIT_STEPS = ['Location', 'Photos', 'Details'];
const LITE_STEPS = ['Choose a location', 'Write something'];
const PREVIEW_LABEL = 'Preview';

// Sub-reviews (parking / viewpoint / trailhead) use a lightweight 2-step
// flow: no photo upload, and the write step hides visit time/duration.
// The sub-review entry points always pass a relation, so that's what we
// key on. Top-level reviews keep the full 3-step flow.
//
// Standalone top-level creation (no relation, no parentId, not a draft)
// gets an extra "Preview" step after Write so the user can see the
// composed review in the detail-page layout and attach related spots
// before anything hits the server. The preview renders from a local
// draft; nothing is POSTed until the user clicks Done — at which point
// we POST the parent, then each draft sub sequentially, then navigate
// to the new detail page.
const SpotReviewComposer = ({isDraft, onClose, parentId, relation, onDraftSubmit, editReview, allowFutureDates}) => {
  const isEdit = !!editReview;
  const isLite = !!relation;
  const showPreviewStep = (!isLite && !isDraft && !parentId && !relation);
  const steps = isEdit
    ? [...EDIT_STEPS, PREVIEW_LABEL]
    : isLite
      ? LITE_STEPS
      : showPreviewStep
        ? [...FULL_STEPS, PREVIEW_LABEL]
        : FULL_STEPS;

  const history = useHistory();
  const spotReviewContext = useContext(SpotReviewContext);
  const { addSpotReview, getSpotReview, updateSpotReview, deleteSpotReviewPhoto } = spotReviewContext;

  const alertContext = useContext(AlertContext);
  const { setAlert } = alertContext;

  const [activeStep, setActiveStep] = useState(0);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const [placeName, setPlaceName] = useState(isEdit ? (editReview.spot_name || "") : "");
  const [placeAddress, setPlaceAddress] = useState(isEdit ? (editReview.spot_address || "") : "");
  const [geo, setGeo] = useState({ lat: -33.860664, lng: 151.208138 });

  // In edit mode, seed previewUrls with existing photo URLs and files with
  // null placeholders. When the user adds new photos they append real File
  // objects. On delete we call the server for existing photos (identified by
  // having a matching entry in existingPhotoIds) or just splice for new ones.
  const editPhotos = isEdit ? (editReview.photos || []) : [];
  const [files, setFiles] = useState(editPhotos.map(() => null));
  const [previewUrls, setPreviewUrls] = useState(editPhotos.map((p) => p.image_link));
  // Parallel array: existingPhotoIds[i] is the server photo id for that slot,
  // or null if it's a newly uploaded file.
  const [existingPhotoIds, setExistingPhotoIds] = useState(editPhotos.map((p) => p.id));

  // Convert Unix timestamp (seconds) to datetime-local string (YYYY-MM-DDTHH:mm)
  const unixToDatetimeLocal = (unix) => {
    if (!unix || unix <= 0) return "";
    const d = new Date(unix * 1000);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  // Calculate duration in minutes from two Unix timestamps, snapped to nearest option
  const calcDuration = (from, to) => {
    if (!from || !to || to <= from) return "";
    const mins = Math.round((to - from) / 60);
    const options = [30, 60, 90, 120, 180, 240, 360, 480, 720, 1440];
    const closest = options.reduce((prev, curr) =>
      Math.abs(curr - mins) < Math.abs(prev - mins) ? curr : prev
    );
    return String(closest);
  };

  const [spotReview, setSpotReview] = useState(isEdit ? {
    title: editReview.title || "",
    content: editReview.content || "",
    placeId: editReview.spot_place_id || "",
    visitStartTime: unixToDatetimeLocal(editReview.visit_from),
    visitDuration: calcDuration(editReview.visit_from, editReview.visit_to),
    price: editReview.price || "",
    placeTypes: editReview.place_types || [],
    category: editReview.category || "",
    parentId: parentId || null,
    relation: editReview.relation || relation || null,
  } : {
    title: "",
    content: "",
    placeId: "",
    visitStartTime: "",
    visitDuration: "",
    placeTypes: [],
    parentId: parentId || null,
    relation: relation || null,
  });

  // Draft state for the preview-before-POST flow. `draftParent` is the
  // composed top-level review (in draft shape: both camelCase and
  // server-style fields — see WriteContentCreationStep.draftCommon).
  // `draftSubs` collects any related spots the user attaches on the
  // preview step. Nothing here lives on the server until handleDone runs.
  const [draftParent, setDraftParent] = useState(null);
  const [draftSubs, setDraftSubs] = useState([]);
  const [isPosting, setIsPosting] = useState(false);

  // mimic lifecycle, call when values of dependencies change (create mode only)
  useEffect(() => {
    if (isEdit) return;
    setSpotReview({
      title: "",
      content: "",
      placeId: "",
      visitStartTime: "",
      visitDuration: "",
      placeTypes: [],
      parentId: parentId || null,
      relation: relation || null,
    });
  }, [spotReviewContext, parentId, relation, isEdit]);

  const backButton =
    <Button
      color="inherit"
      disabled={activeStep === 0 || isPosting}
      onClick={handleBack}
      sx={{ mr: 1 }}
    >
      Back
    </Button>;

  // Write step submitted — stash the draft and advance to preview.
  // No server call yet. "Back" from preview brings the user here with
  // the local state intact, since we never cleared it.
  const handlePreview = (draft) => {
    setDraftParent(draft);
    setActiveStep(steps.length - 1);
  };

  // Pill on the preview composed a sub — stash it in draftSubs. The
  // SpotReviewPreview re-renders from subReviews so it shows up under
  // "Related spots" immediately.
  const handleAddDraftSub = (sub) => {
    setDraftSubs((prev) => [...prev, sub]);
  };

  // Build the server-shape payload addSpotReview expects. The reducer's
  // addSpotReview builds a multipart FormData; photos go in via `photos`
  // (the raw File list) and the rest as fields. Our draft stores files
  // on `.files`, so pipe those in as `photos`.
  const draftToPayload = (d, extra = {}) => ({
    title: d.title,
    content: d.content,
    placeId: d.placeId,
    visitStartTime: d.visitStartTime,
    visitDuration: d.visitDuration,
    price: d.price,
    category: d.category,
    placeTypes: d.placeTypes || [],
    photos: d.files || [],
    ...extra,
  });

  const handleDone = async () => {
    if (!draftParent || isPosting) return;
    setIsPosting(true);
    try {
      const savedParent = await addSpotReview(draftToPayload(draftParent));
      if (!savedParent || !savedParent.id) {
        setAlert("Failed to post review. Please try again.", "error");
        setIsPosting(false);
        return;
      }

      // POST subs sequentially so id ordering matches the user's action
      // order. Parallel POSTs would be faster but race on the BE's
      // sub-order logic and complicate error attribution.
      let subFailure = false;
      for (const sub of draftSubs) {
        const saved = await addSpotReview(
          draftToPayload(sub, {
            parentId: savedParent.id,
            relation: sub.relation,
          })
        );
        if (!saved || !saved.id) subFailure = true;
      }

      if (subFailure) {
        setAlert(
          "Review posted, but one or more related spots failed. You can add them again from the review page.",
          "warning"
        );
      } else {
        setAlert("Successfully posted", "success");
      }

      // The POST response doesn't include photos / spot_name / spot_address —
      // only the DB id and the fields we sent up. Refetch via GET so the
      // cached entry in spotReviews is complete before the detail page
      // reads from it (otherwise the user lands on a page with no image).
      await getSpotReview(savedParent.id);

      history.push(`/review/${savedParent.id}`);
      if (onClose) onClose();
    } catch (e) {
      setAlert("Failed to post review. Please try again.", "error");
      setIsPosting(false);
    }
  };

  // Called when deleting a photo at a given index. If the photo is an
  // existing server photo (has an id), call DELETE on the server first.
  const handleDeletePhoto = async (idx) => {
    const photoId = existingPhotoIds[idx];
    if (photoId) {
      const result = await deleteSpotReviewPhoto(editReview.id, photoId);
      if (!result.ok) {
        setAlert("Failed to delete photo", "error");
        return;
      }
    }
    setFiles((prev) => prev.filter((_, i) => i !== idx));
    setPreviewUrls((prev) => prev.filter((_, i) => i !== idx));
    setExistingPhotoIds((prev) => prev.filter((_, i) => i !== idx));
  };

  // Edit mode: read-only location step
  const editStepOne = isEdit ? (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1, flex: 1 }}>
      <Box>
        <Box component="h3" sx={{ m: 0, fontSize: '1.25rem', fontWeight: 600, color: 'text.primary' }}>
          Location
        </Box>
        <Box sx={{ fontSize: '0.85rem', color: 'text.secondary', mt: 0.5 }}>
          Location cannot be changed after creation.
        </Box>
      </Box>
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1.5, p: 2,
        border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'grey.50',
      }}>
        <Box>
          <Typography sx={{ fontWeight: 500 }}>{placeName || 'Unknown location'}</Typography>
          {placeAddress && (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>{placeAddress}</Typography>
          )}
        </Box>
      </Box>
      <Box sx={{ flexGrow: 1 }} />
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <Button onClick={handleNext} variant="contained"
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3 }}>
          Next
        </Button>
      </Box>
    </Box>
  ) : null;

  const stepOne =
    <FindPlaceCreationStep
      spotReview={spotReview}
      setSpotReview={setSpotReview}
      placeName={placeName}
      setPlaceName={setPlaceName}
      setPlaceAddress={setPlaceAddress}
      geo={geo}
      setGeo={setGeo}
      handleNext={handleNext}
    />;

  const stepTwo =
    <UploadPhotoCreationStep
      spotReview={spotReview}
      setSpotReview={setSpotReview}
      files={files}
      setFiles={setFiles}
      previewUrls={previewUrls}
      setPreviewUrls={setPreviewUrls}
      backButton={backButton}
      handleNext={handleNext}
      isEdit={isEdit}
      existingPhotoIds={existingPhotoIds}
      setExistingPhotoIds={setExistingPhotoIds}
      onDeletePhoto={isEdit ? handleDeletePhoto : undefined}
    />;

  // Edit mode submit handler for WriteContentCreationStep
  const [promoteDialogOpen, setPromoteDialogOpen] = useState(false);
  const [pendingEditFields, setPendingEditFields] = useState(null);

  const buildEditFields = () => {
    const fields = {
      title: spotReview.title,
      content: spotReview.content,
      price: spotReview.price,
    };
    if (spotReview.visitStartTime) {
      fields.visitStartTime = Math.floor(new Date(spotReview.visitStartTime).getTime() / 1000);
    }
    if (spotReview.visitDuration) fields.visitDuration = spotReview.visitDuration;
    if (spotReview.category) fields.category = spotReview.category;
    const newFiles = files.filter((f) => f !== null);
    if (newFiles.length > 0) fields.photos = newFiles;
    return fields;
  };

  const submitEdit = async (fields) => {
    const result = await updateSpotReview(editReview.id, fields);
    if (result.ok) {
      setAlert("Review updated", "success");
      await getSpotReview(editReview.id);
      if (onClose) onClose();
    } else {
      setAlert(result.msg || "Failed to update", "error");
    }
  };

  const handleEditSubmit = async () => {
    const fields = buildEditFields();
    // If this is a trip-only review and new photos are being added,
    // ask the user if they want to promote it to a full review.
    const hasNewPhotos = files.some((f) => f !== null);
    if (editReview.trip_only && hasNewPhotos) {
      setPendingEditFields(fields);
      setPromoteDialogOpen(true);
      return;
    }
    await submitEdit(fields);
  };

  const handlePromoteConfirm = async () => {
    setPromoteDialogOpen(false);
    if (pendingEditFields) {
      await submitEdit({ ...pendingEditFields, promote: true });
      setPendingEditFields(null);
    }
  };

  const handlePromoteCancel = async () => {
    setPromoteDialogOpen(false);
    // Save without promoting — photos are added but review stays trip-only.
    if (pendingEditFields) {
      await submitEdit(pendingEditFields);
      setPendingEditFields(null);
    }
  };

  const stepThree =
    <WriteContentCreationStep
      spotReview={spotReview}
      setSpotReview={setSpotReview}
      files={files}
      setFiles={setFiles}
      previewUrls={previewUrls}
      placeName={placeName}
      placeAddress={placeAddress}
      backButton={backButton}
      isDraft={isDraft}
      onClose={onClose}
      onDraftSubmit={onDraftSubmit}
      onPreview={(showPreviewStep || isEdit) ? handlePreview : undefined}
      lite={isLite}
      isEdit={isEdit}
      onEditSubmit={isEdit ? handleEditSubmit : undefined}
      allowFutureDates={allowFutureDates}
    />;

  const previewStep = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
          Preview your review
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Here's how it will look. Attach related spots below — parking,
          a viewpoint, or a trailhead — then hit Done to post.
        </Typography>
      </Box>
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <SpotReviewPreview
          review={draftParent}
          subReviews={draftSubs}
          allowAddRelated
          addRelatedDraftSubmit={handleAddDraftSub}
        />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1 }}>
        {backButton}
        <Button
          variant="contained"
          onClick={isEdit ? handleEditSubmit : handleDone}
          disabled={isPosting || !draftParent}
          startIcon={isPosting ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3 }}
        >
          {isPosting ? 'Saving…' : isEdit ? 'Save' : 'Submit'}
        </Button>
      </Box>
    </Box>
  );

  // Lite flow skips the photo upload step entirely; users can still
  // attach a place name + title + note for the parking lot / viewpoint.
  const stepsContent = isEdit
    ? [editStepOne, stepTwo, stepThree, previewStep]
    : isLite
      ? [stepOne, stepThree]
      : showPreviewStep
        ? [stepOne, stepTwo, stepThree, previewStep]
        : [stepOne, stepTwo, stepThree];

  return (
    <Box sx={{ width: "100%", minWidth: 0, display: 'flex', flexDirection: 'column', flex: 1 }}>
      <Stepper activeStep={activeStep} sx={{
        mb: 2,
        '& .MuiStepLabel-label': { fontSize: { xs: '0.75rem', sm: '0.875rem' } },
      }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {stepsContent[activeStep]}
      </Box>
      {isEdit && (
        <Dialog open={promoteDialogOpen} onClose={() => setPromoteDialogOpen(false)}>
          <DialogTitle>Make review visible?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Adding photos will make this review visible in Local Finds and My Journeys.
              It was previously only visible within its trip. Continue?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handlePromoteCancel}>Keep trip-only</Button>
            <Button onClick={handlePromoteConfirm} variant="contained">Make visible</Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default SpotReviewComposer;
